<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import CollapseBox from '@/presentation/components/collapse-box/CollapseBox.vue';
import NewStartButton from '@/presentation/components/common/new-start-button/NewStartButton.vue';
import ExportButton from '@/presentation/components/common/export-button/ExportButton.vue';
import DisclosureNav from '@/presentation/pages/ai-disclosure/components/DisclosureNav.vue';
import GoalsSetting from './GoalsSetting.vue';
import DisclosureParagraph from './DisclosureParagraph.vue';
import DisclosureEditTypeList from './DisclosureEditTypeList.vue';
import DisclosureParagraphEdit from './DisclosureParagraphEdit.vue';
import HistoryHeader from '@/presentation/components/history/HistoryHeader.vue';
// import Feedback from '@analytics/shared/components/feedback/Feedback.vue'; todo
import DisclosureTitle from '@/presentation/pages/ai-disclosure/components/DisclosureTitle.vue';
import IconDisclosure from '@/presentation/components/icons/IconDisclosure.vue';
import ButtonStop from '@/presentation/components/common/button/ButtonStop.vue';
import ButtonRegenerate from '@/presentation/components/common/button/ButtonRegenerate.vue';
import '@patsnap-ui/icon/assets/solid/export.svg';
import '@patsnap-ui/icon/assets/solid/ArrowLeft.svg';
import '@patsnap-ui/icon/assets/solid/ArrowRight.svg';
import { useAiDisclosureResult } from '@/composables/use-ai-disclosure-result';
import { useHistory } from '@/composables/use-history';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import _ from 'lodash';
import { useFloating } from '@floating-ui/vue';
import { offset } from '@floating-ui/dom';
import type {
    AIDisclosureParagraphEditType,
    AIDisclosureParagraphType,
} from '@/services/ai-disclosure/type/ai-disclosure.enum';
import type { DisclosureResult, NavLeaf } from '@/services/ai-disclosure/type/ai-disclosure.type';
import { StreamStatus } from '@/services/ai-disclosure/type/ai-disclosure.enum';
import { navTree } from '@/presentation/pages/ai-disclosure/components/constants';

const SCROLL_CONTAINER_ID = '#disclosure';
const HIGHLIGHT_ID = 'disclosure-highlight';
const AUTO_TOGGLE_NAV_WIDTH = 1680;
const TOP_GAP = 0;
const SCROLL_TOP_GAP = 0;

let rangeCache = null;
let scrollContainer = null;

const { t } = useI18n();
const router = useRouter();
const { historyData } = useHistory();
const {
    aiDisclosureResultService,
    aiDisclosureResultData,
} = useAiDisclosureResult();

function showParagraph(paragraph: DisclosureResult) {
    let previousGenerated;
    const index = navTree.findIndex(item => item.type === paragraph.type);
    if (index === 0) {
        previousGenerated = true;
    } else {
        // 取上一个节点
        let previous = navTree[index - 1];
        if (previous.childrenTypes) {
            previous = navTree[index - 2];
        }
        previousGenerated = aiDisclosureResultData.value.disclosureResult.find(
            p => p.paragraphType === previous.type
        )?.isGenerated ?? false;
    }

    return (paragraph.childrenTypes || !paragraph.isHidden) && previousGenerated;
}

const floatingHiddenPosition = {
    x: 0,
    y: 0,
    top: -9999,
    left: -9999,
    bottom: 0,
    right: 0,
    width: 0,
    height: 0,
};

const selectedText = ref<string>('');
const streamStatus = ref<StreamStatus>(StreamStatus.ABORTED);
const showNav = ref<boolean>(true);
const editingType = ref<AIDisclosureParagraphEditType>();
const editingParagraph = ref<AIDisclosureParagraphType | undefined>(undefined);
const isSelection = ref<boolean>(false);
const offsetTopList = ref([]);
const currentLeaf = ref(navTree[0].type);
const floatingStrongStyles = ref({});
const reference = ref({
    getBoundingClientRect: () => {
        return floatingHiddenPosition;
    },
});
const showFloatComp = ref(false);
const floatIsChoosing = ref(true);
const floating = ref(null);

const { floatingStyles } = useFloating(reference, floating, {
    // middleware: [flip({
    //     alignment: 'bottom',
    // })],
    // whileElementsMounted: autoUpdate,
    middleware: [offset(4)],
});

const { historyService } = useHistory();

function toggleFloatingStatus(editType: AIDisclosureParagraphEditType) {
    floatIsChoosing.value = false;
    showFloatComp.value = false;
    editingType.value = editType;

    if (isSelection.value) {
        // 编辑框强制左对齐
        const originalTransform = floatingStyles.value.transform;
        const newTransform = originalTransform.replace(/\((\d+)px,/, '(0px,');

        floatingStrongStyles.value = {
            transform: newTransform,
        };
    } else {
        const dom = document.getElementById(editingParagraph.value);

        if (dom) {
            reference.value = {
                getBoundingClientRect: () => dom.getBoundingClientRect(),
            };
        }
    }
}

const disclosureResult = computed<DisclosureResult[]>(() => {
    return navTree.map((item: NavLeaf) => {
        const paragraph = aiDisclosureResultData.value.disclosureResult.find(
            p => p.paragraphType === item.type
        );
        if (paragraph) {
            return {
                ...item,
                paragraphType: paragraph.paragraphType,
                content: paragraph?.content ?? '',
                isGenerated: paragraph?.isGenerated ?? false,
                isHidden: !paragraph.paragraphType || aiDisclosureResultData.value.hidden.includes(paragraph.paragraphType),
                title: t(item.titleKey),
                ...(item.parentTitleKey && { parentTitle: t(item.parentTitleKey) }),
            };
        }

        return {
            ...item,
            content: '',
            title: t(item.titleKey),
            ...(item.parentTitleKey && { parentTitle: t(item.parentTitleKey) }),
        };
    });
});

const noEditStatus = computed<boolean>(() => {
    return streamStatus.value !== StreamStatus.FINISH;
});

// const feedbackParams = computed(() => {
//     const taskId = historyData.value.historyDetailTemp.taskId;
//     return {
//         product: 'ANALYTICS-AI-LAB',
//         module: 'https://analytics.zhihuiya.com/ai-lab/#/disclosure',
//         page_params: `?taskId=${taskId}`,
//         query: `{"task_Id":${taskId}}`,
//         question: '',
//         answer: '',
//     };
// });

function adjustHeight() {
    scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'smooth',
    });
}

watch(() => disclosureResult.value, () => {
    if (scrollContainer && streamStatus.value === StreamStatus.PROCESSING) {
        adjustHeight();
    }

    const generatedParagraph = disclosureResult.value.filter(item => item.isGenerated);

    if (generatedParagraph.length === disclosureResult.value.length - 2) {
        streamStatus.value = StreamStatus.FINISH;
    } else {
        streamStatus.value = StreamStatus.PROCESSING;
    }
}, {
    immediate: true,
});

function stopGenerate() {
    streamStatus.value = StreamStatus.ABORTED;
    aiDisclosureResultService.stopGenerate();
}

function executeRegenerate() {
    aiDisclosureResultService.generateResult();
}

function handleExport() {
    aiDisclosureResultService.exportDisclosure(disclosureResult.value);
}

async function handleNewStart() {
    window.open('/ai-disclosure/#/disclosure/new', '_blank');
}

function leafClick(leaf: NavLeaf) {
    const element = document.getElementById(`disclosure-paragraph-${leaf.type}`);
    if (!element) {
        return;
    }

    const parent = document.querySelector(SCROLL_CONTAINER_ID);
    parent.scrollTo({
        top: element.offsetTop - SCROLL_TOP_GAP,
        behavior: 'smooth',
    });
}

function clearHighlight() {
    selectedText.value = '';

    const span = document.getElementById(HIGHLIGHT_ID);
    if (span) {
        span.outerHTML = span.textContent || '';
    }
}

function hiddenFloating() {
    floatingStrongStyles.value = {};
    reference.value = {
        getBoundingClientRect: () => floatingHiddenPosition,
    };

    floatIsChoosing.value = true;
    showFloatComp.value = false;

    clearHighlight();
}

function handleSelectionChange(range: Range, paragraphType: AIDisclosureParagraphType) {
    editingParagraph.value = paragraphType;
    isSelection.value = true;
    rangeCache = range;
    const startContainer = range.startContainer;
    const endContainer = range.endContainer;

    if (startContainer === endContainer) {
        hiddenFloating();
        selectedText.value = window.getSelection().toString();
        nextTick(() => {
            showFloatComp.value = true;

            const span = document.createElement('span');
            span.id = HIGHLIGHT_ID;
            span.style.backgroundColor = '#DDEBF8';

            range.surroundContents(span);

            reference.value = {
                getBoundingClientRect: () => range.getBoundingClientRect(),
            };
        });
    }
}

function handleClickOptimizationBtn(dom: HTMLElement, paragraphType: AIDisclosureParagraphType) {
    hiddenFloating();
    floatIsChoosing.value = true;
    showFloatComp.value = true;
    editingParagraph.value = paragraphType;
    isSelection.value = false;
    selectedText.value = disclosureResult.value.find(item => item.type === paragraphType)?.content ?? '';

    reference.value = {
        getBoundingClientRect: () => dom.getBoundingClientRect(),
    };
}

function updateContent(paragraphType: AIDisclosureParagraphType, content: string) {
    aiDisclosureResultService.updateDisclosureResult(paragraphType, content);
}

function handleReplaceEdit(newText: string) {
    if (!editingParagraph.value) return;
    if (isSelection.value) {
        const newNode = document.createTextNode(newText);
        rangeCache.deleteContents();
        rangeCache.insertNode(newNode);
        const content = rangeCache.startContainer.textContent;

        aiDisclosureResultService.updateDisclosureResult(editingParagraph.value, content.trim());
    } else {
        aiDisclosureResultService.updateDisclosureResult(editingParagraph.value, newText);
    }

    hiddenFloating();
}

function handleAdditionContent(newText: string) {
    if (!editingParagraph.value) return;
    aiDisclosureResultService.updateDisclosureResult(editingParagraph.value, newText, true);

    hiddenFloating();
}

function handleParagraphVisibleChange(resultItem: DisclosureResult) {
    if (resultItem.paragraphType) {
        aiDisclosureResultService.toggleParagraphVisibleStatus(resultItem.paragraphType);
    }
}

const scrollHandle = ({ target }) => {
    const curScrollTop = target.scrollTop;

    offsetTopList.value.forEach(item => {
        if (curScrollTop + TOP_GAP >= item.offsetTop) {
            currentLeaf.value = item.type;
        }
    });
};
const debounceScrollHandle = _.debounce(scrollHandle, 100);

const resizeHandle = () => {
    showNav.value = window.innerWidth > AUTO_TOGGLE_NAV_WIDTH;
};

const debounceResizeHandle = _.debounce(resizeHandle, 100);

function initOffsetTopList() {
    offsetTopList.value = disclosureResult.value.map(paragraph => {
        const node = document.getElementById(`disclosure-paragraph-${paragraph.type}`);

        return {
            type: paragraph.type,
            offsetTop: node.offsetTop,
        };
    });
}

const debounceInitOffsetTopList = _.debounce(initOffsetTopList, 100);

onMounted(() => {
    nextTick(() => {
        initOffsetTopList();
        resizeHandle();
    });
    scrollContainer = document.querySelector(SCROLL_CONTAINER_ID);
    scrollContainer.addEventListener('scroll', debounceScrollHandle);
    window.addEventListener('resize', debounceResizeHandle);
});

onUnmounted(() => {
    scrollContainer.removeEventListener('scroll', debounceScrollHandle);
    window.removeEventListener('resize', debounceResizeHandle);
});
</script>

<template>
    <div class="disclosure-result flex w-full justify-start">
        <div class="disclosure-result-body relative w-full">
            <HistoryHeader class="sticky top-0" :untitledText="t('aiDisclosure.untitled')">
                <template #type>
                    <IconDisclosure class="th-btn-icon mr-1 h-10 w-10" />
                </template>

                <template #suffix>
                    <ExportButton class="mr-4" @click.native="handleExport" />

                    <NewStartButton @click="handleNewStart" />
                </template>
            </HistoryHeader>

            <DisclosureNav
                v-if="!noEditStatus"
                :tree="disclosureResult"
                :currentLeaf="currentLeaf"
                :showCompleteNav="showNav"
                @leafClick="leafClick"
                @visibleChange="handleParagraphVisibleChange"
            />

            <div class="flex w-full justify-center pb-[112px]">
                <div class="relative">
                    <DisclosureTitle :title="aiDisclosureResultData.title" :showHover="true" />

                    <DisclosureParagraph
                        v-for="item in disclosureResult"
                        v-show="showParagraph(item)"
                        :key="item.type"
                        :paragraph="item"
                        :noEdit="noEditStatus"
                        @resize="debounceInitOffsetTopList"
                        @visibleChange="handleParagraphVisibleChange"
                        @selectionChange="handleSelectionChange"
                        @clickOptimizationBtn="handleClickOptimizationBtn"
                        @updateContent="updateContent"
                    />

                    <div v-if="noEditStatus" class="fixed bottom-0 flex w-[640px] justify-center">
                        <ButtonStop v-if="streamStatus === StreamStatus.PROCESSING" @click.native="stopGenerate" />
                        <ButtonRegenerate
                            v-if="streamStatus === StreamStatus.ABORTED"
                            :desc="t('aiDisclosureGlobal.globalBtn.regenerate')"
                            @click.native="executeRegenerate"
                        />
                    </div>

                    <div v-else class="pt-8 text-center">
                        <!-- <span class="pb-2 text-sm text-grey-90">
                            {{
                                t('aiDisclosure.feedbackLabel')
                            }}
                        </span>

                        <Feedback class="justify-center" type="Disclosure" :params="feedbackParams" /> -->
                    </div>

                    <div>
                        <div
                            ref="floating"
                            :style="{ ...floatingStyles, ...floatingStrongStyles }"
                            class="z-30"
                        >
                            <DisclosureEditTypeList
                                v-if="floatIsChoosing && showFloatComp"
                                :paragraphType="editingParagraph"
                                :isSelection="isSelection"
                                @toggleFloatingStatus="toggleFloatingStatus"
                                @hiddenFloating="hiddenFloating"
                            />

                            <DisclosureParagraphEdit
                                v-if="!floatIsChoosing"
                                :selectedText="selectedText"
                                :isSelection="isSelection"
                                :editType="editingType"
                                :editingParagraph="editingParagraph"
                                :floatingStyles="floatingStyles"
                                @discard="hiddenFloating"
                                @replace="handleReplaceEdit"
                                @addition="handleAdditionContent"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="collapse-box-wrap relative">
            <CollapseBox
                class="sticky right-0 top-0"
                :title="t('aiDisclosure.AIAssistant')"
                :floatBtnLabel="t('aiDisclosure.AIAssistant')"
            >
                <GoalsSetting @submit="hiddenFloating" />
            </CollapseBox>
        </div>
    </div>
</template>
